/**
 * Seeded pseudo-random number generation.
 *
 * The demo is a sales artefact as much as a product: an executive who sees a
 * different "Crew Disruption Recovery" cost on Tuesday than on Monday stops
 * trusting the screen. Everything downstream of this file is therefore a pure
 * function of an integer seed — no `Math.random`, no `Date.now`.
 */

export interface Rng {
  /** Uniform float in [0, 1). */
  next(): number;
  /** Uniform integer in [min, max] inclusive. */
  int(min: number, max: number): number;
  /** Uniform float in [min, max). */
  float(min: number, max: number): number;
  /** True with probability `p`. */
  chance(p: number): boolean;
  /** Uniformly pick one element. Throws on an empty list rather than returning undefined. */
  pick<T>(items: readonly T[]): T;
  /** Pick by relative weight. Weights need not sum to 1. */
  weighted<T>(entries: readonly (readonly [T, number])[]): T;
  /** Fisher–Yates copy; the input is not mutated. */
  shuffle<T>(items: readonly T[]): T[];
  /** Pick `count` distinct elements (or all of them when the list is shorter). */
  sample<T>(items: readonly T[], count: number): T[];
  /** Approximately normal via the mean of four uniforms, clamped to [min, max]. */
  around(mean: number, spread: number, min: number, max: number): number;
  /** A named child stream, so adding a generator never shifts an unrelated one. */
  fork(label: string): Rng;
}

/** mulberry32 — 32-bit state, good enough distribution, trivially reproducible. */
function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** FNV-1a over a label, used to derive child-stream seeds. */
export function hashLabel(label: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < label.length; i += 1) {
    h ^= label.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

export function createRng(seed: number): Rng {
  const next = mulberry32(seed);

  const rng: Rng = {
    next,
    int: (min, max) => min + Math.floor(next() * (max - min + 1)),
    float: (min, max) => min + next() * (max - min),
    chance: (p) => next() < p,
    pick<T>(items: readonly T[]): T {
      if (items.length === 0) throw new Error('rng.pick: empty list');
      const value = items[Math.floor(next() * items.length)];
      if (value === undefined) throw new Error('rng.pick: index out of range');
      return value;
    },
    weighted<T>(entries: readonly (readonly [T, number])[]): T {
      const total = entries.reduce((sum, entry) => sum + entry[1], 0);
      if (total <= 0) throw new Error('rng.weighted: non-positive total weight');
      let target = next() * total;
      for (const entry of entries) {
        target -= entry[1];
        if (target <= 0) return entry[0];
      }
      const last = entries[entries.length - 1];
      if (last === undefined) throw new Error('rng.weighted: empty list');
      return last[0];
    },
    shuffle<T>(items: readonly T[]): T[] {
      const copy = items.slice();
      for (let i = copy.length - 1; i > 0; i -= 1) {
        const j = Math.floor(next() * (i + 1));
        const a = copy[i];
        const b = copy[j];
        if (a === undefined || b === undefined) continue;
        copy[i] = b;
        copy[j] = a;
      }
      return copy;
    },
    sample<T>(items: readonly T[], count: number): T[] {
      return rng.shuffle(items).slice(0, Math.max(0, Math.min(count, items.length)));
    },
    around(mean, spread, min, max) {
      const sum = next() + next() + next() + next();
      const centred = (sum / 4 - 0.5) * 2; // roughly -1..1, bell-shaped
      return Math.min(max, Math.max(min, mean + centred * spread));
    },
    fork: (label) => createRng((seed ^ hashLabel(label)) >>> 0),
  };

  return rng;
}
